/**
 * SOLOMON CONTEXT
 * Global state management for the entire Solomon application.
 *
 * Day 4 upgrades:
 * - activeMode defaults to 'auto' — Solomon picks the best mode per message
 * - detectedMode is stored on each assistant message (what Solomon actually used)
 * - regenerateWithMode(mode) — user can switch mode and get a new response
 *
 * Day 6 upgrades:
 * - voiceEnabled: persisted to localStorage, drives ElevenLabs TTS on/off
 * - selectedVoiceId: persisted to localStorage, which ElevenLabs voice to use
 * - Removed timer-based isSpeaking simulation — real TTS drives this now
 */

import { createContext, useContext, useReducer, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { prepareImageAttachment } from '../components/ChatInterface/imageDownscale';
import { shouldFetchPending } from './pendingFetch.js';
import { appendStep, mergeMessagePatch } from './messageSteps.js';
import { continuationRequest, shouldFireContinuation, continuationLever } from './continuation.js';

// Module-level store for the last diagram error — accessible during regeneration
// without prop-drilling through the entire component tree
let _lastDiagramError = null;
export function setLastDiagramError(error) { _lastDiagramError = error; }
export function getLastDiagramError() { return _lastDiagramError; }
export function clearLastDiagramError() { _lastDiagramError = null; }

// ── Lightweight frontend retry (mirrors backend withRetry) ──────
async function retryFetch(fn, maxAttempts = 3, baseDelay = 1500) {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const msg    = (err.message || '').toLowerCase();
      const status = err.status || err.statusCode;
      const retryable =
        status === 429 || status === 503 || status === 529 ||
        msg.includes('rate limit') || msg.includes('overloaded') ||
        msg.includes('timeout')   || msg.includes('network');
      if (attempt < maxAttempts && retryable) {
        await new Promise(r => setTimeout(r, baseDelay * attempt));
      } else {
        break;
      }
    }
  }
  throw lastError;
}

// ============================================================
// STATE SHAPE
// ============================================================

const initialState = {
  // Conversation
  messages:        [],
  conversationId:  null,           // hydrated by App.jsx resume effect, not directly
  activeMode:      'auto',  // mode picker UI ripped 2026-06-11 (zero manual picks ever) — auto-detect always; per-message re-route via ModeBadge unaffected

  // Solomon status
  isThinking:      false,
  thinkingModel:   null,    // Which model Solomon is routing to (shown in thinking indicator)
  isSpeaking:      false,
  isListening:     false,
  isLoadingHistory: false,  // True while loading a past conversation from /api/chat/history

  // Voice preferences (Day 6) — read from localStorage on boot
  voiceEnabled:    localStorage.getItem('solomon-voice-enabled') === 'true',
  selectedVoiceId: localStorage.getItem('solomon-voice-id') || '',

  // User
  user: {
    id:   null,
    name: 'Justin',
    role: 'owner',
  },

  // UI
  theme:       'dark',
  sidebarOpen: false,

  // Error
  error: null,
  // Non-error transient notice (e.g. an OAuth sign-in that succeeded). Kept
  // separate from `error` so a success is never announced as an alert.
  notice: null,

  // Action guardrails (Phase 2A Layer 4)
  pendingActions:   [],   // proposed actions awaiting approval
  activeUndoBanner: null, // { actionId, actionType, reversible, undoDeadline }

  // Integrations (Phase 2)
  gmailConnected: false,
};

// ============================================================
// ACTIONS
// ============================================================

export const ACTIONS = {
  // Messages
  ADD_MESSAGE:         'ADD_MESSAGE',
  SET_MESSAGES:        'SET_MESSAGES',
  CLEAR_MESSAGES:      'CLEAR_MESSAGES',
  SET_THINKING_MODEL:  'SET_THINKING_MODEL',
  UPDATE_LAST_MESSAGE: 'UPDATE_LAST_MESSAGE',
  APPEND_TO_LAST_MESSAGE_CONTENT: 'APPEND_TO_LAST_MESSAGE_CONTENT',
  APPEND_LAST_MESSAGE_STEP: 'APPEND_LAST_MESSAGE_STEP',
  REMOVE_LAST_MESSAGE: 'REMOVE_LAST_MESSAGE',

  // Conversation
  SET_CONVERSATION_ID: 'SET_CONVERSATION_ID',
  NEW_CONVERSATION:    'NEW_CONVERSATION',

  // Solomon status
  SET_THINKING:        'SET_THINKING',
  SET_SPEAKING:        'SET_SPEAKING',
  SET_LISTENING:       'SET_LISTENING',
  SET_LOADING_HISTORY: 'SET_LOADING_HISTORY',

  // Voice preferences (Day 6)
  SET_VOICE_ENABLED:   'SET_VOICE_ENABLED',
  SET_VOICE_ID:        'SET_VOICE_ID',

  // User
  SET_USER:            'SET_USER',

  // UI
  TOGGLE_THEME:        'TOGGLE_THEME',
  TOGGLE_SIDEBAR:      'TOGGLE_SIDEBAR',
  SET_ERROR:           'SET_ERROR',
  CLEAR_ERROR:         'CLEAR_ERROR',
  SET_NOTICE:          'SET_NOTICE',
  CLEAR_NOTICE:        'CLEAR_NOTICE',

  // Integrations (Phase 2)
  SET_GMAIL_CONNECTED:    'SET_GMAIL_CONNECTED',

  // Action guardrails (Phase 2A)
  ADD_PENDING_ACTION:     'ADD_PENDING_ACTION',
  REMOVE_PENDING_ACTION:  'REMOVE_PENDING_ACTION',
  SET_UNDO_BANNER:        'SET_UNDO_BANNER',
  CLEAR_UNDO_BANNER:      'CLEAR_UNDO_BANNER',
};

// ============================================================
// REDUCER
// ============================================================

function solomonReducer(state, action) {
  switch (action.type) {

    case ACTIONS.ADD_MESSAGE:
      return { ...state, messages: [...state.messages, action.payload] };

    case ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };

    case ACTIONS.CLEAR_MESSAGES:
      return { ...state, messages: [] };

    case ACTIONS.UPDATE_LAST_MESSAGE: {
      const msgs = [...state.messages];
      if (msgs.length > 0) {
        msgs[msgs.length - 1] = mergeMessagePatch(msgs[msgs.length - 1], action.payload);
      }
      return { ...state, messages: msgs };
    }

    case ACTIONS.APPEND_TO_LAST_MESSAGE_CONTENT: {
      const msgs = [...state.messages];
      if (msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        msgs[msgs.length - 1] = { ...last, content: (last.content || '') + action.payload };
      }
      return { ...state, messages: msgs };
    }

    // Merged thinking/steps card: one entry per distinct tool-call status
    // event on the streaming placeholder. Dedupe (consecutive identical
    // tool+query) lives in appendStep so it's covered by messageSteps.test.js
    // without a JSX parse of this file.
    case ACTIONS.APPEND_LAST_MESSAGE_STEP: {
      const msgs = [...state.messages];
      if (msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        msgs[msgs.length - 1] = { ...last, steps: appendStep(last.steps, action.payload) };
      }
      return { ...state, messages: msgs };
    }

    case ACTIONS.REMOVE_LAST_MESSAGE: {
      const msgs = [...state.messages];
      if (msgs.length > 0) msgs.pop();
      return { ...state, messages: msgs };
    }

    case ACTIONS.SET_CONVERSATION_ID:
      // Persist active conversation so refresh/reopen resumes it
      if (action.payload) {
        localStorage.setItem('solomon-active-conversation', action.payload);
      } else {
        localStorage.removeItem('solomon-active-conversation');
      }
      return { ...state, conversationId: action.payload };

    case ACTIONS.NEW_CONVERSATION:
      // Clear the persisted active conversation when starting fresh
      localStorage.removeItem('solomon-active-conversation');
      return {
        ...state,
        messages:       [],
        conversationId: null,
        isThinking:     false,
        isSpeaking:     false,
        isListening:    false,
        error:          null,
      };

    case ACTIONS.SET_THINKING:
      return { ...state, isThinking: action.payload, thinkingModel: action.payload ? state.thinkingModel : null };

    case ACTIONS.SET_THINKING_MODEL:
      return { ...state, thinkingModel: action.payload };

    case ACTIONS.SET_SPEAKING:
      return { ...state, isSpeaking: action.payload };

    case ACTIONS.SET_LISTENING:
      return { ...state, isListening: action.payload };

    case ACTIONS.SET_LOADING_HISTORY:
      return { ...state, isLoadingHistory: action.payload };

    // Day 6: voice preferences — also persist to localStorage
    case ACTIONS.SET_VOICE_ENABLED:
      localStorage.setItem('solomon-voice-enabled', String(action.payload));
      return { ...state, voiceEnabled: action.payload };

    case ACTIONS.SET_VOICE_ID:
      localStorage.setItem('solomon-voice-id', action.payload);
      return { ...state, selectedVoiceId: action.payload };

    case ACTIONS.SET_USER:
      return { ...state, user: { ...state.user, ...action.payload } };

    case ACTIONS.TOGGLE_THEME:
      return { ...state, theme: state.theme === 'dark' ? 'light' : 'dark' };

    case ACTIONS.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case ACTIONS.SET_NOTICE:
      return { ...state, notice: action.payload };

    case ACTIONS.CLEAR_NOTICE:
      return { ...state, notice: null };

    // Integrations
    case ACTIONS.SET_GMAIL_CONNECTED:
      return { ...state, gmailConnected: action.payload };

    // Action guardrails
    case ACTIONS.ADD_PENDING_ACTION:
      // Dedup by id: fetchPendingActions re-fetches ALL pending rows after a turn,
      // so an action already present (still-pending from a prior turn, or added by
      // its own panel) must not append a second card with a colliding React key.
      if (state.pendingActions.some(a => a.id === action.payload?.id)) return state;
      return { ...state, pendingActions: [...state.pendingActions, action.payload] };

    case ACTIONS.REMOVE_PENDING_ACTION:
      return { ...state, pendingActions: state.pendingActions.filter(a => a.id !== action.payload) };

    case ACTIONS.SET_UNDO_BANNER:
      return { ...state, activeUndoBanner: action.payload };

    case ACTIONS.CLEAR_UNDO_BANNER:
      return { ...state, activeUndoBanner: null };

    default:
      return state;
  }
}

// ============================================================
// CONTEXT
// ============================================================

const SolomonContext = createContext(null);

export function SolomonProvider({ children }) {
  const [state, dispatch] = useReducer(solomonReducer, initialState);

  // Always-latest snapshot of `state` for use inside stable useCallbacks. Reading
  // state.messages directly inside a callback forces state.messages into its dep
  // array, which gives the callback a NEW identity on every streamed chunk (each
  // chunk mutates state.messages). That new identity flows down as a prop to every
  // memoized MessageBubble and defeats the memo (all bubbles re-render + re-parse
  // per chunk). Callbacks that only need the latest state at call-time read it from
  // this ref instead, so their identity stays stable across the whole stream.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Day 9: Auth token for all API calls
  const { authFetch } = useAuth();

  // Holds the AbortController for the in-flight streaming reply so the Stop
  // button can abort the SSE fetch client-side. Pure client abort — issues NO
  // new backend request (the turn's pending_reinforcement row was already
  // created at retrieval time and resolves on the next user action).
  const streamAbortRef = useRef(null);

  // Settings panel. Lifted out of App.jsx (spec §5) so any component — an
  // ActionCard deep in the message list included — can open a section without
  // a prop chain. `settingsSection` deep-links the accordion and is cleared on
  // every toggle, so a generic "Open settings" never inherits the section a
  // capability attention row asked for earlier.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState(null);
  const toggleSettings = useCallback(() => {
    setSettingsOpen((o) => !o);
    setSettingsSection(null);
  }, []);
  const openSettings = useCallback((section = null) => {
    setSettingsSection(section || null);
    setSettingsOpen(true);
  }, []);
  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    setSettingsSection(null);
  }, []);

  // Theme stamping REMOVED (2026-08-26): App.jsx owns the theme — it hydrates
  // from localStorage('solomon-theme') and stamps <html data-theme>. This
  // effect was a second, competing writer whose hardcoded-'dark' initial
  // state re-stamped OVER the user's preference after App's stamp on every
  // mount (provider effects run after child effects), forcing dark on load
  // and feeding the theme races that baked dark palettes into light-mode
  // charts/diagrams (Issue 55; prod screenshot 2026-08-26). state.theme and
  // TOGGLE_THEME remain as inert legacy — nothing consumes them (SettingsPanel
  // receives App's theme as a prop). Do not re-add a stamper here.

  // ---- Action Creators ----

  const addMessage = useCallback((message) => {
    const enriched = {
      id:            message.id            || `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      role:          message.role,
      content:       message.content,
      model:         message.model         || null,
      timestamp:     message.timestamp     || new Date().toISOString(),
      mode:          message.mode          || state.activeMode,
      detectedMode:  message.detectedMode  || null,
      // Day 5: routing metadata
      routingReason: message.routingReason || null,
      confidence:    message.confidence    ?? null,
      flag:          message.flag          || null,
      // Day 7: did RAG inject memory context for this response?
      memoryUsed:    message.memoryUsed    ?? false,
      // Live web answers: numbered sources behind [n] citations
      liveSources:   message.liveSources   || null,
      // Briefing metadata
      isBriefing:    message.isBriefing   || false,
      briefingType:  message.briefingType || null,    // 'morning' | 'evening' | null
      // Streaming state: true while SSE chunks are still arriving for this bubble
      isStreaming:   message.isStreaming  || false,
      // Output-quality capture: server messages.id (rating anchor) + render-complete time (latency feature)
      responseId:    message.responseId   || null,
      renderedAt:    message.renderedAt   || null,
      // Resumed-turn marker (spec 2026-09-21 §3.6) — MessageBubble hides
      // Regenerate on one. Listed here because this whitelist DROPS any key
      // it does not name.
      isContinuation: message.isContinuation || false,
    };
    dispatch({ type: ACTIONS.ADD_MESSAGE, payload: enriched });
    return enriched;
  }, [state.activeMode]);

  // ---- Edit & resend (Task 3) — route a "pending draft" into InputBar ----
  // InputBar owns its text value in internal state; this lets a prior user
  // message inject its content into the input box for editing + resending as a
  // brand-new turn. Resending is just a normal send — no special backend path.
  const [pendingDraft, setPendingDraft] = useState(null);
  const editMessage = useCallback((content) => setPendingDraft(content), []);
  const consumeDraft = useCallback(() => setPendingDraft(null), []);

  // ---- Ephemeral chat attachments (sticky-for-conversation, never persisted) ----
  const [attachments, setAttachments] = useState([]); // [{ name, charCount, wrapped, truncated }]

  const usedAttachBudget = attachments.reduce((s, a) => s + a.charCount, 0);

  const attachDocument = useCallback(async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('usedBudget', String(usedAttachBudget));
    try {
      const res = await authFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/chat/attachment`, {
        method: 'POST',
        body: form,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        dispatch({ type: ACTIONS.SET_ERROR, payload: json.error || 'Could not attach file.' });
        return;
      }
      setAttachments(prev => [...prev, json.data]); // { name, charCount, wrapped, truncated }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Could not attach file. Check your connection and try again.' });
    }
  }, [authFetch, usedAttachBudget]);

  const clearAttachment = useCallback((idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  }, []);
  const clearAttachments = useCallback(() => setAttachments([]), []);

  // ---- Pending images (ephemeral ONE-TURN vision attachments) ----
  // Unlike doc attachments (sticky for the conversation), images ride only the
  // next send, then clear — vision tokens are too expensive to resend per turn.
  const MAX_PENDING_IMAGES = 3;
  const [pendingImages, setPendingImages] = useState([]); // [{ name, mediaType, dataBase64, byteSize }]

  const attachImage = useCallback(async (file) => {
    let overCap = false;
    setPendingImages(prev => {
      overCap = prev.length >= MAX_PENDING_IMAGES;
      return prev;
    });
    if (overCap) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Up to ${MAX_PENDING_IMAGES} images per message — remove one first.` });
      return;
    }
    try {
      const prepared = await prepareImageAttachment(file); // downscaled client-side
      setPendingImages(prev =>
        prev.length >= MAX_PENDING_IMAGES ? prev : [...prev, prepared]
      );
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message || 'Could not read that image.' });
    }
  }, []);

  const clearPendingImage = useCallback((idx) => {
    setPendingImages(prev => prev.filter((_, i) => i !== idx));
  }, []);
  const clearPendingImages = useCallback(() => setPendingImages([]), []);

  // ---- Core send function (used by regenerateWithMode + non-streaming fallback) ----
  const callBackend = useCallback(async ({ content, mode, conversationId, modelOverride = null, regenerate = false, diagramError = null }) => {
    return retryFetch(async () => {
      const response = await authFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/chat`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message:        content,
          conversationId: conversationId,
          mode:           mode,
          userId:         state.user.id,
          modelOverride:  modelOverride || undefined,
          regenerate:     regenerate || undefined,
          diagramError:   diagramError || undefined,
          attachments:    attachments.length ? attachments.map(a => ({ name: a.name, wrapped: a.wrapped })) : undefined,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const err = new Error(errData.error || errData.message || `Server error: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return response.json();
    }, 3, 1500);
  }, [state.user.id, authFetch, attachments]);

  // ---- Streaming send (SSE) — used by sendMessage normal-chat path ----
  // Server emits: event: chunk { text } | event: done { ...meta } | event: error { ...err }
  // No retry wrapper here — once any bytes have arrived we can't replay; the
  // caller decides how to surface mid-stream errors to the user.
  const callBackendStream = useCallback(async ({ content, mode, conversationId, onChunk, onStatus, continuationActionId = null }) => {
    // Client-side abort handle for the Stop button. Stored so stopGeneration()
    // can cancel the fetch; cleared in finally below regardless of outcome.
    const controller = new AbortController();
    streamAbortRef.current = controller;

    try {
    const response = await authFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/chat`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      // A continuation carries NO text, no attachments and no images — the
      // server refuses a body that smuggles a user turn (spec §3.2 step 1).
      body: JSON.stringify(continuationActionId ? {
        conversationId,
        continuationActionId,
        stream: true,
      } : {
        message:        content,
        conversationId: conversationId,
        mode:           mode,
        userId:         state.user.id,
        stream:         true,
        attachments:    attachments.length ? attachments.map(a => ({ name: a.name, wrapped: a.wrapped })) : undefined,
        images:         pendingImages.length ? pendingImages.map(im => ({ name: im.name, mediaType: im.mediaType, dataBase64: im.dataBase64 })) : undefined,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const err = new Error(errData.error || errData.message || `Server error: ${response.status}`);
      err.status = response.status;
      throw err;
    }

    if (!response.body) {
      throw new Error('Streaming not supported by this environment (no response.body)');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let donePayload = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines (\n\n)
      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        let eventType = 'message';
        let dataStr   = '';
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:'))      eventType = line.slice(6).trim();
          else if (line.startsWith('data:'))  dataStr  += line.slice(5).trim();
        }
        if (!dataStr) continue;

        let data;
        try { data = JSON.parse(dataStr); }
        catch (parseErr) {
          console.warn('[Solomon/Stream] Failed to parse SSE data:', parseErr.message, dataStr.slice(0, 100));
          continue;
        }

        if (eventType === 'chunk' && typeof data.text === 'string') {
          onChunk?.(data.text);
        } else if (eventType === 'status') {
          // Live-search progress (e.g. { state:'searching', query }) — purely
          // cosmetic; unknown states are ignored by the handler.
          onStatus?.(data);
        } else if (eventType === 'done') {
          donePayload = data;
        } else if (eventType === 'error') {
          const err = new Error(data.error || 'Stream error');
          err.details = data.details;
          throw err;
        }
      }
    }

    return donePayload;
    } finally {
      // Clear the abort handle whether the stream finished, threw, or was
      // aborted by the Stop button.
      streamAbortRef.current = null;
    }
  }, [state.user.id, authFetch, attachments, pendingImages]);

  // ---- Detect greeting triggers ----
  const isMorningGreeting = (text) => {
    const t = text.toLowerCase().trim();
    return /\b(good\s*morning|morning\s+solomon|morning|buenos\s+dias|gm\s+solomon|morning,?\s*sol)\b/.test(t);
  };

  const isEveningGreeting = (text) => {
    const t = text.toLowerCase().trim();
    return /\b(good\s*night|goodnight|end\s+of\s+day|wrap\s+up\s+my\s+day|evening\s+solomon|gn\s+solomon|night,?\s*sol|gn)\b/.test(t);
  };

  // Detect "regenerate briefing" triggers — returns { match: boolean, type: 'morning'|'evening'|'auto' }
  const isRegenerateBriefing = (text) => {
    const t = text.toLowerCase().trim();
    if (!/\b(regenerate|refresh|new)\s+(morning\s+)?briefing|regenerate\s+evening\s+briefing\b/.test(t)) {
      return { match: false, type: null };
    }
    if (/\bmorning\s+briefing\b/.test(t)) return { match: true, type: 'morning' };
    if (/\bevening\s+briefing\b/.test(t)) return { match: true, type: 'evening' };
    // Auto: before 5 PM → morning, after → evening
    const hour = new Date().getHours();
    return { match: true, type: hour < 17 ? 'morning' : 'evening' };
  };

  // Local date helper — matches backend's America/New_York caching
  const getLocalDateStr = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local

  // ---- Fetch briefings with a 50-second timeout + full error reporting ----
  // 50s aligns with the backend worst case since the provider ladder (Group 1,
  // 2026-09-14): 4s gather + 14s per rung × 3 rungs (Claude → GPT → Gemini)
  // + margin. On a normal day Claude answers inside the first 14s; the window
  // only matters on a dead-provider day — exactly when the ladder needs room
  // to serve the briefing from another provider instead of the client
  // aborting to an error toast.
  // Backend self-degrades to placeholder text on its own internal timeout, so
  // the frontend should give it room to land that fallback rather than abort.
  const fetchBriefing = useCallback(async (endpoint, label, force = false) => {
    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const url = `${API_BASE}/api/briefing/${endpoint}${force ? '?force=true' : ''}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 50_000);
    try {
      console.log(`[Solomon] Fetching ${label} from ${url}...`);
      const res = await authFetch(url, { signal: controller.signal });
      clearTimeout(timeout);
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        console.error(`[Solomon] ${label} API returned ${res.status}: ${errBody.slice(0, 200)}`);
        return { content: null, error: `API ${res.status}` };
      }
      const data = await res.json();
      if (!data.content) {
        console.warn(`[Solomon] ${label} API returned OK but content is empty/null:`, JSON.stringify(data).slice(0, 200));
        return { content: null, error: 'Empty response' };
      }
      console.log(`[Solomon] ${label} received: ${data.content.length} chars`);
      return { content: data.content, model: data.model ?? null, error: null };
    } catch (err) {
      clearTimeout(timeout);
      const reason = err.name === 'AbortError' ? 'timed out (50s)' : err.message;
      console.error(`[Solomon] ${label} fetch failed: ${reason}`);
      return { content: null, error: reason };
    }
  }, [authFetch]);

  const fetchAndDisplayBriefing = useCallback(() => fetchBriefing('today', 'Morning briefing'), [fetchBriefing]);
  const fetchEveningBriefing = useCallback(() => fetchBriefing('evening', 'Evening briefing'), [fetchBriefing]);

  // Group 5 PR C: the Discord briefing link (?open=briefing&date=YYYY-MM-DD) —
  // that day's morning briefing from GET /api/briefing/:date, shown as the same
  // message the auto-briefing block in App.jsx builds. Identity: addMessage's
  // only dep is state.activeMode, which nothing ever changes (the mode picker
  // was ripped 2026-06-11), so this follows fetchBriefing → authFetch → token.
  const showBriefingForDate = useCallback(async (date) => {
    const result = await fetchBriefing(date, 'Morning briefing');
    if (result?.content) {
      addMessage({ role: 'assistant', content: result.content, model: result.model, detectedMode: 'briefing', isBriefing: true, briefingType: 'morning' });
    } else {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'No briefing for that date' });
    }
    return result;
  }, [fetchBriefing, addMessage]);

  // ---- Send new message (mode = 'auto' by default) ----
  const sendMessage = useCallback(async (content) => {
    // 1. Add user message immediately
    addMessage({ role: 'user', content });

    // 2. Set thinking state
    dispatch({ type: ACTIONS.SET_THINKING, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    // ── Regenerate briefing intercept (checked FIRST) ──────────
    const regen = isRegenerateBriefing(content);
    if (regen.match) {
      const type = regen.type;
      const endpoint = type === 'morning' ? 'today' : 'evening';
      const storageKey = type === 'morning' ? 'solomon-last-briefing-date' : 'solomon-evening-briefing-date';

      console.log(`[Solomon] Regenerating ${type} briefing (force=true)`);
      localStorage.removeItem(storageKey);

      const { content: briefingText, model: briefingModel, error } = await fetchBriefing(endpoint, `${type} briefing (regenerated)`, true);

      if (briefingText) {
        localStorage.setItem(storageKey, getLocalDateStr());
        addMessage({
          role:         'assistant',
          content:      briefingText,
          model:        briefingModel,
          detectedMode: 'briefing',
          isBriefing:   true,
          briefingType: type,
        });
      } else {
        const reason = error || 'unknown error';
        console.error(`[Solomon] ${type} briefing regeneration failed:`, reason);
        addMessage({
          role: 'assistant',
          content: `I couldn't regenerate your ${type} briefing right now (${reason}). Try again in a moment.`,
          model: 'system',
        });
      }

      dispatch({ type: ACTIONS.SET_THINKING, payload: false });
      return;
    }

    // ── Briefing intercept ────────────────────────────────────
    // If this is a morning or evening greeting, ALWAYS handle it as a
    // briefing request. NEVER fall through to the regular chat endpoint.
    const todayStr = getLocalDateStr();
    const morningMatch = isMorningGreeting(content);
    const eveningMatch = isEveningGreeting(content);

    if (morningMatch || eveningMatch) {
      const type = morningMatch ? 'morning' : 'evening';
      const storageKey = morningMatch ? 'solomon-last-briefing-date' : 'solomon-evening-briefing-date';
      const lastDate = localStorage.getItem(storageKey);

      console.log(`[Solomon] ${type} greeting detected:`, { content: content.slice(0, 40), lastDate, todayStr, alreadyShown: lastDate === todayStr });

      // Already shown today — acknowledge without regenerating
      if (lastDate === todayStr) {
        addMessage({
          role: 'assistant',
          content: morningMatch
            ? 'You already got your morning briefing today. Say "regenerate briefing" if you want a fresh one.'
            : 'You already got your evening wrap-up today. Say "regenerate briefing" if you want a fresh one.',
          model: 'system',
        });
        dispatch({ type: ACTIONS.SET_THINKING, payload: false });
        return;
      }

      // Fetch the briefing
      const fetchFn = morningMatch ? fetchAndDisplayBriefing : fetchEveningBriefing;
      const { content: briefingText, model: briefingModel, error } = await fetchFn();

      if (briefingText) {
        localStorage.setItem(storageKey, todayStr);
        addMessage({
          role:         'assistant',
          content:      briefingText,
          model:        briefingModel,
          detectedMode: 'briefing',
          isBriefing:   true,
          briefingType: type,
        });
      } else {
        // Briefing failed — show error, do NOT fall through to chat
        const reason = error || 'unknown error';
        console.error(`[Solomon] ${type} briefing failed:`, reason);
        dispatch({ type: ACTIONS.SET_ERROR, payload: `Briefing unavailable: ${reason}` });
        addMessage({
          role: 'assistant',
          content: `I couldn't load your ${type} briefing right now (${reason}). Try again in a moment, or say "regenerate briefing" later.`,
          model: 'system',
        });
      }

      dispatch({ type: ACTIONS.SET_THINKING, payload: false });
      return; // ALWAYS return — greetings never go to regular chat
    }

    // ── Normal chat (streaming) ───────────────────────────────
    // Insert an empty assistant bubble that chunks will append into. Mark it
    // isStreaming so MessageBubble can render incomplete chart/diagram blocks
    // as placeholders rather than raw JSON.
    let placeholderAdded = false;
    try {
      addMessage({ role: 'assistant', content: '', isStreaming: true });
      placeholderAdded = true;

      const data = await callBackendStream({
        content,
        mode:           state.activeMode,
        conversationId: state.conversationId,
        onChunk: (text) => {
          dispatch({ type: ACTIONS.APPEND_TO_LAST_MESSAGE_CONTENT, payload: text });
        },
        onStatus: (status) => {
          if (status?.state === 'searching') {
            const query = status.query || ' ';
            const tool = status.tool || null;
            dispatch({
              type: ACTIONS.UPDATE_LAST_MESSAGE,
              payload: { searchingQuery: query, searchingTool: tool },
            });
            // Accumulate into `steps` so the merged card can render the full
            // tool-call history (live while streaming, collapsed once content
            // starts) instead of only the latest in-flight status.
            dispatch({
              type: ACTIONS.APPEND_LAST_MESSAGE_STEP,
              payload: { tool, query },
            });
          }
        },
      });

      // Images are one-turn-only: the send consumed them, clear the chips.
      setPendingImages([]);

      if (data?.conversationId && !state.conversationId) {
        dispatch({ type: ACTIONS.SET_CONVERSATION_ID, payload: data.conversationId });
      }

      // Patch the now-complete bubble with metadata from the `done` event.
      if (data) {
        dispatch({
          type: ACTIONS.UPDATE_LAST_MESSAGE,
          payload: {
            model:         data.model,
            detectedMode:  data.detectedMode  || 'conversation',
            routingReason: data.routingReason || null,
            confidence:    data.confidence    ?? null,
            flag:          data.flag          || null,
            memoryUsed:    data.memoryUsed    ?? false,
            liveSources:   data.liveSources   || null,
            generatedFiles: data.generatedFiles || null,
            searchingQuery: null,
            searchingTool:  null,
            isStreaming:   false,
            // Output-quality capture: rating anchor (server messages.id) + render-complete time
            responseId:    data.messageId     || null,
            renderedAt:    Date.now(),
          },
        });
        dispatch({ type: ACTIONS.SET_THINKING_MODEL, payload: data.model || null });
        if (shouldFetchPending(data)) fetchPendingActions();
      } else {
        // Stream ended without a `done` event — flip streaming off anyway so
        // the bubble doesn't stay in placeholder mode forever.
        dispatch({ type: ACTIONS.UPDATE_LAST_MESSAGE, payload: { isStreaming: false } });
      }

    } catch (err) {
      // Clean stop: the user hit the Stop button, aborting the SSE fetch.
      // Keep the partial assistant text that already streamed in via onChunk —
      // just flip streaming off and surface NO error. No new request is issued.
      if (err.name === 'AbortError') {
        // The message (with any images) was already sent — stop only halts the
        // reply, so images still count as consumed.
        setPendingImages([]);
        dispatch({ type: ACTIONS.UPDATE_LAST_MESSAGE, payload: { isStreaming: false } });
        return; // finally still runs → SET_THINKING false; ref already cleared
      }

      console.error('[Solomon] Chat error:', err);
      const friendlyMsg = err.status === 401
        ? 'Session expired. Please refresh the page and log back in.'
        : err.status === 429
        ? 'I\'m getting a lot of requests right now. Give me just a moment and try again.'
        : err.status >= 500
        ? 'I\'m having trouble reaching my AI models right now. Try again in a few seconds.'
        : 'Something went wrong on my end. Please try again.';

      dispatch({ type: ACTIONS.SET_ERROR, payload: friendlyMsg });
      if (placeholderAdded) {
        // Replace the empty placeholder rather than appending a second bubble.
        dispatch({
          type: ACTIONS.UPDATE_LAST_MESSAGE,
          // steps: [] — a failed turn must not wear a "Searched the web" strip over its error text.
          payload: { content: friendlyMsg, model: 'error', isStreaming: false, steps: [] },
        });
      } else {
        addMessage({ role: 'assistant', content: friendlyMsg, model: 'error' });
      }
    } finally {
      dispatch({ type: ACTIONS.SET_THINKING, payload: false });
    }
    // fetchPendingActions is intentionally omitted here: it is declared later in
    // this file, so adding it to this array TDZ-crashes at render. Its freshness is
    // safe transitively — callBackendStream depends on authFetch, so any authFetch
    // change recreates this callback and re-captures a current fetchPendingActions.
    // Keep callBackendStream's authFetch dep; do NOT add fetchPendingActions here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMessage, callBackendStream, fetchAndDisplayBriefing, fetchEveningBriefing, state.conversationId, state.activeMode]);

  // ---- Regenerate last response in a different mode or with a different model ----
  const regenerateWithMode = useCallback(async (forcedMode, modelOverride = null) => {
    const msgs = stateRef.current.messages;

    let lastAssistantIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') { lastAssistantIdx = i; break; }
    }
    if (lastAssistantIdx === -1) return;

    let lastUserIdx = -1;
    for (let i = lastAssistantIdx - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') { lastUserIdx = i; break; }
    }
    if (lastUserIdx === -1) return;

    const userMessage = msgs[lastUserIdx].content;

    const trimmed = msgs.slice(0, lastAssistantIdx);
    dispatch({ type: ACTIONS.SET_MESSAGES, payload: trimmed });
    dispatch({ type: ACTIONS.SET_THINKING, payload: true });
    dispatch({ type: ACTIONS.SET_THINKING_MODEL, payload: modelOverride || null });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      // Check for diagram error from the previous response
      const diagramError = getLastDiagramError();
      clearLastDiagramError();

      const data = await callBackend({
        content:        userMessage,
        mode:           forcedMode,
        conversationId: stateRef.current.conversationId,
        modelOverride,
        regenerate:     true,   // tell backend to drop the prior turn
        diagramError,           // include diagram error context if any
      });

      if (data.conversationId && !stateRef.current.conversationId) {
        dispatch({ type: ACTIONS.SET_CONVERSATION_ID, payload: data.conversationId });
      }

      dispatch({
        type: ACTIONS.ADD_MESSAGE,
        payload: {
          id:           `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          role:         'assistant',
          content:      data.message,
          model:        data.model,
          timestamp:    new Date().toISOString(),
          mode:         forcedMode,
          detectedMode: data.detectedMode || forcedMode,
          routingReason: data.routingReason || null,
          confidence:    data.confidence ?? null,
          flag:          data.flag || null,
          userOverride: true,
          memoryUsed:   data.memoryUsed ?? false,
          liveSources:  data.liveSources || null,
          // Output-quality capture (regenerated turn gets a fresh messages.id)
          responseId:   data.messageId || null,
          renderedAt:   Date.now(),
        },
      });

      if (shouldFetchPending(data)) fetchPendingActions();

    } catch (err) {
      console.error('[Solomon] Regenerate error:', err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message || 'Failed to regenerate response.' });
    } finally {
      dispatch({ type: ACTIONS.SET_THINKING, payload: false });
    }
    // fetchPendingActions intentionally omitted (declared later → TDZ if added). Safe
    // because callBackend depends on authFetch, so any authFetch change recreates this
    // callback and re-captures a current fetchPendingActions. Do NOT add it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callBackend]);

  // ---- Output-quality capture: record a feedback signal (rating | copy) ----
  // Fire-and-forget; NEVER blocks the UI or surfaces errors (acceptance #9).
  // confidence + latency ride along on ratings as features — never folded into correctness.
  const submitFeedback = useCallback((responseId, signalType, extra = {}) => {
    if (!responseId || !signalType) return;
    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const body = { response_id: responseId, signal_type: signalType };
    if (signalType === 'rating') {
      body.value = extra.value;
      if (extra.confidence != null) body.confidence = extra.confidence;
      if (extra.latencyMs != null) body.latency_ms = extra.latencyMs;
    }
    authFetch(`${API_BASE}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});  // capture must never affect chat UX
  }, [authFetch]);

  // Stop the in-flight streaming reply. Pure client-side abort of the SSE
  // fetch — issues NO new backend request. The partial reply already shown is
  // kept; sendMessage's catch treats the resulting AbortError as a clean stop.
  const stopGeneration = useCallback(() => {
    streamAbortRef.current?.abort();
  }, []);

  const newConversation = useCallback(() => {
    dispatch({ type: ACTIONS.NEW_CONVERSATION });
    setAttachments([]); // attachments are conversation-scoped — drop on fresh chat
    setPendingImages([]); // pending images don't survive a fresh chat either
  }, []);

  const toggleTheme = useCallback(() => {
    dispatch({ type: ACTIONS.TOGGLE_THEME });
  }, []);

  // Day 6: voice preference setters
  const setVoiceEnabled = useCallback((enabled) => {
    dispatch({ type: ACTIONS.SET_VOICE_ENABLED, payload: enabled });
  }, []);

  const setSelectedVoiceId = useCallback((id) => {
    dispatch({ type: ACTIONS.SET_VOICE_ID, payload: id });
  }, []);

  // ---- Action guardrail handlers (Phase 2A Layer 4) ----

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  /**
   * The fire conditions a continuationRequest cannot answer by itself: the
   * card may belong to another conversation, a stream may already own the
   * last bubble, and the operator may have turned the lever off.
   */
  const mayFireContinuation = useCallback((request) => shouldFireContinuation({
    request,
    currentConversationId: stateRef.current.conversationId,
    streamInFlight: !!streamAbortRef.current || stateRef.current.isThinking,
    leverStatus: continuationLever(),
  }), []);

  /**
   * Resume the turn an approved action belongs to (spec §3.6). Reached only
   * from the shared approve function, which is the single approve path for
   * every caller. No user bubble is drawn — this is the same turn continuing.
   */
  const runContinuation = useCallback(async ({ conversationId, continuationActionId }) => {
    dispatch({ type: ACTIONS.SET_THINKING, payload: true });
    let placeholderAdded = false;
    try {
      addMessage({ role: 'assistant', content: '', isStreaming: true, isContinuation: true });
      placeholderAdded = true;
      const done = await callBackendStream({
        conversationId,
        continuationActionId,
        onChunk: (text) => dispatch({ type: ACTIONS.APPEND_TO_LAST_MESSAGE_CONTENT, payload: text }),
      });
      dispatch({
        type: ACTIONS.UPDATE_LAST_MESSAGE,
        payload: {
          model:         done?.model || null,
          detectedMode:  done?.detectedMode || 'conversation',
          routingReason: done?.routingReason || null,
          confidence:    done?.confidence ?? null,
          flag:          done?.flag || null,
          liveSources:   done?.liveSources || null,
          responseId:    done?.messageId || null,
          isStreaming:   false,
          isContinuation: true,
          renderedAt:    Date.now(),
        },
      });
      if (done?.model) dispatch({ type: ACTIONS.SET_THINKING_MODEL, payload: done.model });
      // A continuation turn can itself propose a gated call — surface its card.
      if (shouldFetchPending(done)) fetchPendingActions();
    } catch (err) {
      // Stop button: a clean client abort, not a failure. Keep what streamed.
      if (err.name === 'AbortError') {
        dispatch({ type: ACTIONS.UPDATE_LAST_MESSAGE, payload: { isStreaming: false } });
        return;
      }
      // 409 = the server refused: another tab consumed it, the depth backstop
      // fired, or the lever is off. Silent by design — the result is still in
      // the Phase 0 state header for the operator's next message.
      if (err.status === 409) {
        if (placeholderAdded) dispatch({ type: ACTIONS.REMOVE_LAST_MESSAGE });
        return;
      }
      console.error('[Solomon] Continuation failed:', err);
      const friendly = 'I could not pick that back up automatically — ask me to continue and I\'ll have the result.';
      dispatch({ type: ACTIONS.SET_ERROR, payload: friendly });
      if (placeholderAdded) {
        dispatch({ type: ACTIONS.UPDATE_LAST_MESSAGE, payload: { content: friendly, model: 'error', isStreaming: false, steps: [] } });
      }
    } finally {
      dispatch({ type: ACTIONS.SET_THINKING, payload: false });
    }
    // fetchPendingActions is declared later in this file — referencing it in the
    // BODY is safe (it runs at call time), but it must stay out of this dep
    // array or the const TDZ-crashes at render. Same rule as sendMessage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callBackendStream, addMessage, dispatch]);

  const approveAction = useCallback(async (actionId) => {
    const res = await authFetch(`${API_BASE}/api/actions/${actionId}/approve`, { method: 'POST' });
    if (!res.ok) {
      // Surface the executor's actual error (e.g. "Twilio is not configured: ...")
      // instead of a generic message. Backend has already marked the action
      // status='failed' on executor failure — clear our local pending row so
      // the now-stale ActionCard goes away and operators don't try to cancel
      // an action the server already terminated.
      let errMsg = 'Approval failed';
      try {
        const body = await res.json();
        if (body?.error) errMsg = body.error;
      } catch { /* non-JSON body, keep generic */ }
      dispatch({ type: ACTIONS.REMOVE_PENDING_ACTION, payload: actionId });
      // The card is unmounted by the line above, so the card cannot be where
      // the operator learns this failed (browser audit 2026-09-21: it vanished
      // silently). The toast outlives it. Friendly copy only — never the raw error.
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'That action did not go through — nothing was run. Ask me to try it again.' });
      throw new Error(errMsg);
    }
    const { data } = await res.json();

    dispatch({ type: ACTIONS.REMOVE_PENDING_ACTION, payload: actionId });
    dispatch({
      type: ACTIONS.SET_UNDO_BANNER,
      payload: {
        actionId:     data.id,
        actionType:   data.action_type,
        reversible:   data.reversible,
        undoDeadline: data.undo_deadline,
      },
    });

    // image_generate: the executor persisted an assistant message carrying the
    // [image:] tokens (server-authoritative — spec 2026-08-26 §3.2 step 5).
    // result_text is deliberately a token-free model note, so the UI refetches
    // the conversation to show the images NOW (review restructure 2026-08-27).
    // Mapping mirrors App.jsx's history loader.
    if (data.action_type === 'image_generate' && stateRef.current.conversationId) {
      try {
        const hres = await authFetch(`${API_BASE}/api/chat/history/${stateRef.current.conversationId}`);
        if (hres.ok) {
          const hdata = await hres.json();
          const msgs = (hdata.messages || []).map(m => ({
            id:           m.id,
            responseId:   m.id,
            role:         m.role,
            content:      m.content,
            model:        m.model || null,
            confidence:   m.confidence ?? null,
            liveSources:  m.liveSources || null,
            timestamp:    m.timestamp || m.created_at,
            detectedMode: m.mode || null,
            isBriefing:   m.mode === 'briefing',
          }));
          dispatch({ type: ACTIONS.SET_MESSAGES, payload: msgs });
        }
      } catch (err) {
        console.warn('[Solomon] post-approval refresh failed (images appear on next load):', err.message);
      }
    }

    // Auto-continue (spec §3.1). The trigger sits HERE, in the shared context
    // function, so main chat, ResearchPanel, LeadSearchPanel and alwaysAllowTool
    // are all covered by construction — the fan-out miss this design avoids.
    const cont = continuationRequest(data);
    if (cont && mayFireContinuation(cont)) await runContinuation(cont);

    return data;
  }, [authFetch, dispatch, mayFireContinuation, runContinuation]);

  const alwaysAllowTool = useCallback(async (actionId, serverId, toolName) => {
    // 1) Bless the tool free-read so FUTURE calls auto-run. Ordered first so a
    //    later approve failure still leaves the trust recorded (recoverable).
    const bless = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/tools`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toolName, classification: 'read' }),
    });
    if (!bless.ok) {
      const b = await bless.json().catch(() => ({}));
      throw new Error(b.error || 'Could not always-allow this tool');
    }
    // 2) Approve the currently-queued call so THIS one runs now (Phase 0 returns its result).
    return approveAction(actionId);
  }, [authFetch, approveAction, API_BASE]);

  /** Grant, then approve — the same two-step shape as alwaysAllowTool. */
  const allowToolForChat = useCallback(async (actionId, serverId, toolName, conversationId) => {
    const res = await authFetch(`${API_BASE}/api/mcp/conversations/${conversationId}/grants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serverId, toolName }),
    });
    if (!res.ok) {
      const { error } = await res.json().catch(() => ({}));
      throw new Error(error || 'grant_failed');
    }
    return approveAction(actionId);
  }, [authFetch, approveAction, API_BASE]);

  const cancelAction = useCallback(async (actionId) => {
    const res = await authFetch(`${API_BASE}/api/actions/${actionId}/cancel`, { method: 'POST' });
    if (!res.ok) {
      let errMsg = 'Cancel failed';
      try {
        const body = await res.json();
        if (body?.error) errMsg = body.error;
      } catch { /* non-JSON body, keep generic */ }
      // Whatever the failure mode, the user's intent (don't run this action)
      // is best served by clearing the local pending row — server-side
      // idempotency handles the rest on retry.
      dispatch({ type: ACTIONS.REMOVE_PENDING_ACTION, payload: actionId });
      // The card is unmounted by the line above, so the card cannot be where
      // the operator learns this failed (browser audit 2026-09-21: it vanished
      // silently). The toast outlives it. Friendly copy only — never the raw error.
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'That action did not go through — nothing was run. Ask me to try it again.' });
      throw new Error(errMsg);
    }
    dispatch({ type: ACTIONS.REMOVE_PENDING_ACTION, payload: actionId });
  }, [authFetch, dispatch]);

  const undoAction = useCallback(async (actionId) => {
    const res = await authFetch(`${API_BASE}/api/actions/${actionId}/undo`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to undo action');
    }
    dispatch({ type: ACTIONS.CLEAR_UNDO_BANNER });
    addMessage({
      role: 'assistant',
      content: 'Action undone successfully.',
      model: 'system',
    });
  }, [authFetch, dispatch, addMessage]);

  const dismissUndoBanner = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_UNDO_BANNER });
  }, [dispatch]);

  const addPendingAction = useCallback((action) => {
    if (!action || action.status !== 'pending') return;
    dispatch({ type: ACTIONS.ADD_PENDING_ACTION, payload: action });
  }, [dispatch]);

  const fetchPendingActions = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/actions/pending`);
      if (!res.ok) return;
      const { data } = await res.json();
      (Array.isArray(data) ? data : []).forEach((a) => addPendingAction(a));
    } catch { /* fail-soft: cards simply don't appear this turn */ }
  }, [authFetch, addPendingAction]);

  // Cards survive a reload and a conversation switch (spec §5). Before this,
  // fetchPendingActions ran only after a proposing turn, so an approval card
  // vanished on refresh and the action sat pending with nothing to click.
  useEffect(() => {
    if (!state.user) return;
    fetchPendingActions();
    // fetchPendingActions omitted deliberately, as elsewhere in this file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.conversationId]);

  // ---- Gmail integration handlers (Phase 2) ----

  const checkGmailStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/gmail/status`);
      if (res.ok) {
        const { data } = await res.json();
        dispatch({ type: ACTIONS.SET_GMAIL_CONNECTED, payload: data.connected });
      }
    } catch {
      // Non-fatal — Gmail may not be configured
    }
  }, [authFetch, dispatch]);

  const connectGmail = useCallback(async () => {
    const res = await authFetch(`${API_BASE}/api/gmail/auth`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to start Gmail connection');
    }
    const { data } = await res.json();
    // Navigate to Google OAuth — will redirect back after consent
    window.location.href = data.authUrl;
  }, [authFetch]);

  const disconnectGmail = useCallback(async () => {
    const res = await authFetch(`${API_BASE}/api/gmail/disconnect`, { method: 'POST' });
    if (res.ok) {
      dispatch({ type: ACTIONS.SET_GMAIL_CONNECTED, payload: false });
    }
  }, [authFetch, dispatch]);

  // ---- Value ----

  const value = {
    // State
    ...state,

    // Actions
    sendMessage,
    stopGeneration,
    addMessage,
    newConversation,
    toggleTheme,
    regenerateWithMode,
    dispatch,

    // Task 3: edit & resend a prior user message
    pendingDraft,
    editMessage,
    consumeDraft,

    // Day 6: voice
    setVoiceEnabled,
    setSelectedVoiceId,

    // Ephemeral chat attachments (sticky-for-conversation)
    attachments,
    usedAttachBudget,
    attachDocument,
    clearAttachment,
    clearAttachments,

    // Pending images (ephemeral one-turn vision attachments)
    pendingImages,
    attachImage,
    clearPendingImage,
    clearPendingImages,

    // Day 8: briefing
    fetchAndDisplayBriefing,
    showBriefingForDate,   // Group 5 PR C: the ?open=briefing&date= deep link

    // Phase 2A: action guardrails
    addPendingAction,
    fetchPendingActions,
    approveAction,
    alwaysAllowTool,
    allowToolForChat,
    cancelAction,
    undoAction,
    dismissUndoBanner,

    // Phase 2: integrations
    checkGmailStatus,
    connectGmail,
    disconnectGmail,

    // Output-quality capture (response ratings + copy)
    submitFeedback,

    // Settings panel open/section (lifted from App.jsx)
    settingsOpen,
    settingsSection,
    openSettings,
    closeSettings,
    toggleSettings,
  };

  return (
    <SolomonContext.Provider value={value}>
      {children}
    </SolomonContext.Provider>
  );
}

// ============================================================
// HOOK
// ============================================================

export function useSolomon() {
  const ctx = useContext(SolomonContext);
  if (!ctx) throw new Error('useSolomon must be used within <SolomonProvider>');
  return ctx;
}

export default SolomonContext;
